import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Grid,
  HStack,
  SimpleGrid,
  Stack,
  Stat,
  Status,
  Text,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { LuCalendarDays, LuCircleCheck, LuCopy, LuSettings } from 'react-icons/lu'
import { useNavigate, useParams } from 'react-router-dom'
import {
  acceptDemand,
  getMarketplaceDemand,
  getOwnProfile,
  getOwnWorkload,
  markMarketplaceDemandSeen,
  rejectDemand,
  withdrawInterest,
  type SibProfileResponse,
  type WorkloadResponse,
} from '../api'
import { ErrorState } from '../components/ErrorState'
import { LoadingState } from '../components/LoadingState'
import { RejectDemandDialog } from '../components/RejectDemandDialog'
import {
  getTopicLabel,
  type ApiError,
  type MarketplaceDemand,
  type RejectDemandPayload,
} from '../types'
import { InterestSubmittedDialog } from '../components/InterestSubmittedDialog'
import { MarketplaceStatusBadge } from '../components/MarketplaceStatusBadge'
import { getDemandStatusLabel, isDemandOpen } from '../demandStatus'
import { hoursToUnits } from '@/helper/order.helper'
import { Page } from '@/components/layout/Page'
import { WorkloadQuotaCard } from '@/components/common/WorkloadQuotaCard'

function getBucketUnits(demand: MarketplaceDemand, serviceType: string) {
  return demand.buckets?.find(bucket => bucket.serviceType === serviceType)?.requestedUnits ?? 0
}

function formatPeriod(start?: string, end?: string) {
  if (!start && !end) return '—'

  const format = (value?: string) => {
    if (!value) return ''
    const [year, month, day] = value.split('-')
    return `${day}.${month}.${year}`
  }

  return `${format(start)}—${format(end)}`
}

function formatDate(value: string) {
  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return value

  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function getDaysLeft(expiresAt?: string) {
  // The backend does not always send expiresAt; never invent a deadline for it.
  if (!expiresAt) return null

  const today = new Date()
  const expiry = new Date(expiresAt)

  today.setHours(0, 0, 0, 0)
  expiry.setHours(0, 0, 0, 0)

  return Math.max(0, Math.ceil((expiry.getTime() - today.getTime()) / 86_400_000))
}

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <Card.Root>
      <Card.Body>
        <Card.Description>{label}</Card.Description>
        <Card.Title>{value}</Card.Title>
      </Card.Body>
    </Card.Root>
  )
}

function getCapacityFitMessage(capacityFit?: 'green' | 'yellow' | 'red') {
  if (capacityFit === 'yellow') {
    return {
      colorPalette: 'yellow' as const,
      title: 'Leicht über Kontingent',
      text: 'Ihr offenes Kontingent wird leicht überschritten. Eine Annahme ist voraussichtlich trotzdem möglich, bitte melden Sie Ihr Interesse also trotzdem an, sofern es besteht.',
    }
  }

  if (capacityFit === 'red') {
    return {
      colorPalette: 'red' as const,
      title: 'Stark über Kontingent',
      text: 'Ihr offenes Kontingent wird stark überschritten. Eine Annahme ist unwahrscheinlich.',
    }
  }

  return null
}

function getRemainingWorkloadUnits(workload: WorkloadResponse | null, serviceType: string) {
  const hours =
    workload?.buckets.find(bucket => bucket.serviceType === serviceType)?.remainingCapacityHours ??
    0

  return hoursToUnits(serviceType, hours)
}

function calculateCapacityFitForDemand(
  demand: MarketplaceDemand,
  workload: WorkloadResponse | null
): 'green' | 'yellow' | 'red' | undefined {
  if (!workload) return undefined

  let result: 'green' | 'yellow' | 'red' = 'green'

  for (const bucket of demand.buckets ?? []) {
    const remaining = getRemainingWorkloadUnits(workload, bucket.serviceType)
    const requested = bucket.requestedUnits ?? 0

    if (requested <= 0) continue

    if (remaining <= 0) return 'red'

    const ratio = requested / remaining

    if (ratio > 1.05) return 'red'
    if (ratio > 1) result = 'yellow'
  }

  return result
}

export function MarketplaceDemandDetailPage() {
  const { demandId } = useParams<{ demandId: string }>()
  const navigate = useNavigate()

  const [demand, setDemand] = useState<MarketplaceDemand | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAccepting, setIsAccepting] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [isWithdrawing, setIsWithdrawing] = useState(false)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [error, setError] = useState<ApiError | null>(null)
  const [showInterestDialog, setShowInterestDialog] = useState(false)

  const [profile, setProfile] = useState<SibProfileResponse | null>(null)
  const [workload, setWorkload] = useState<WorkloadResponse | null>(null)

  async function loadUserData() {
    const profileResult = await getOwnProfile()
    setProfile(profileResult)

    const year = demand?.periodStart
      ? Number(demand.periodStart.slice(0, 4))
      : new Date().getFullYear()

    const workloadResult = await getOwnWorkload(profileResult.userId, year)
    setWorkload(workloadResult)
  }

  useEffect(() => {
    if (!demand) return
    void loadUserData()
  }, [demand?.id])

  async function loadDemand() {
    if (!demandId) return

    try {
      setIsLoading(true)
      setError(null)
      const result = await getMarketplaceDemand(demandId)
      setDemand(result)
    } catch {
      setError({ message: 'Could not load demand.' })
    } finally {
      setIsLoading(false)
    }
  }

  // Opening the demand clears its marketplace indicator (SIB-855). Fire-and-forget: failing
  // to record it must not block or disturb reading the demand, and the next poll simply
  // keeps showing the dot.
  useEffect(() => {
    if (!demandId) return

    markMarketplaceDemandSeen(demandId).catch(error => {
      console.warn('Failed to mark marketplace demand as seen:', error)
    })
  }, [demandId])

  async function handleAccept() {
    if (!demandId) return

    try {
      setIsAccepting(true)
      setError(null)

      await acceptDemand(demandId)
      await loadDemand()
      setShowInterestDialog(true)
    } catch (err) {
      setError(err as ApiError)
    } finally {
      setIsAccepting(false)
    }
  }

  async function handleReject(payload: RejectDemandPayload) {
    if (!demandId) return

    try {
      setIsRejecting(true)
      setError(null)

      await rejectDemand(demandId, payload)
      setShowRejectDialog(false)
      await loadDemand()
    } catch (err) {
      setError(err as ApiError)
    } finally {
      setIsRejecting(false)
    }
  }

  async function handleWithdrawInterest() {
    if (!demandId) return

    try {
      setIsWithdrawing(true)
      setError(null)

      await withdrawInterest(demandId)
      await loadDemand()
    } catch (err) {
      setError(err as ApiError)
    } finally {
      setIsWithdrawing(false)
    }
  }

  useEffect(() => {
    void loadDemand()
  }, [demandId])

  if (isLoading) {
    return <LoadingState message="Loading demand..." />
  }

  if (!demand) {
    return <ErrorState message={error?.message ?? 'Demand not found.'} onRetry={loadDemand} />
  }

  const daysLeft = getDaysLeft(demand.expiresAt)
  const isOpen = isDemandOpen(demand)
  // SIB-892: the backend accepts interest on every open demand (its
  // ensureDemandOpenForUserAction rule, mirrored by isDemandOpen), so the fallback
  // must not restrict to OFFERED — that locked out every demand another SIB had
  // already shown interest in. allowedActions still wins once the backend sends it.
  const canAccept = isOpen && (demand.allowedActions?.canAccept ?? true)
  const canReject = isOpen && (demand.allowedActions?.canReject ?? true)
  const hasSubmittedInterest =
    demand.userInterestStatus === 'INTERESTED' || demand.sibInterestStatus === 'interested'
  const capacityFit = calculateCapacityFitForDemand(demand, workload)
  const capacityFitMessage = isOpen ? getCapacityFitMessage(capacityFit) : null

  return (
    <>
      <Page.Root hasActionBar>
        <Page.Header
          breadcrumbs={[
            { label: 'Dashboard', to: '/app/user/dashboard' },
            { label: 'Marktplatz', to: '/app/user/marketplace' },
            { label: 'Angebot' },
          ]}
          onBack={() => navigate('/app/user/marketplace')}
          title="Marktplatzangebot"
          titleAddon={<MarketplaceStatusBadge demand={demand} />}
          meta={`${demand.customerName}${
            isOpen && demand.expiresAt ? ` · Rückmeldung bis ${formatDate(demand.expiresAt)}` : ''
          }`}
        />

        <Page.Content>
          {!isOpen && (
            <Alert.Root status="info">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>
                  Dieses Marktplatzangebot ist{' '}
                  {demand.isArchived
                    ? 'beendet'
                    : (getDemandStatusLabel(demand.status)?.toLowerCase() ?? 'abgeschlossen')}
                </Alert.Title>
                <Alert.Description>
                  Eine Interessensbekundung ist nicht mehr möglich.
                </Alert.Description>
              </Alert.Content>
            </Alert.Root>
          )}

          {isOpen && hasSubmittedInterest && (
            <Badge colorPalette="green" variant="solid" size="md">
              <LuCircleCheck />
              Sie haben Ihr Interesse angemeldet
            </Badge>
          )}

          <HStack gap={3}>
            <Button variant="subtle" colorPalette="gray">
              Übersicht
            </Button>
            <Button variant="ghost" colorPalette="gray" disabled>
              Einsatzgebiet
            </Button>
          </HStack>

          {error?.message && (
            <Alert.Root status="error">
              <Alert.Indicator />
              <Alert.Description>{error.message}</Alert.Description>
            </Alert.Root>
          )}

          <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={8}>
            <Card.Root>
              <Card.Body>
                <Stack gap={7}>
                  <Stack gap={1}>
                    <HStack gap={2}>
                      <Card.Title>
                        {demand.customerName ?? demand.bankName ?? demand.serviceRecipient ?? '—'}
                      </Card.Title>
                      {isOpen && daysLeft !== null && (
                        <Status.Root size="sm" colorPalette="blue">
                          <Status.Indicator />
                          Noch {daysLeft} {daysLeft === 1 ? 'Tag' : 'Tage'}
                        </Status.Root>
                      )}
                    </HStack>

                    <HStack gap={1}>
                      <Text>BLZ {demand.bankId ?? '—'}</Text>
                      <LuCopy />
                      <Text>
                        · {demand.postal ?? demand.postal ?? '—'} {demand.city ?? ''} ·{' '}
                        {demand.salesRegion ?? '—'}
                      </Text>
                    </HStack>
                  </Stack>

                  <Stack gap={2}>
                    <Card.Title>Zeitraum der Bedarfsausführung</Card.Title>
                    <HStack>
                      <LuCalendarDays />
                      <Text>
                        {formatPeriod(demand.periodStart, demand.periodEnd)}
                      </Text>
                    </HStack>
                  </Stack>

                  <Stack gap={3}>
                    <Card.Title>Leistungsumfang des Marktplatzangebotes</Card.Title>

                    <SimpleGrid columns={{ base: 1, md: 4 }} gap={3}>
                      <StatBox label="TOJ" value={`${getBucketUnits(demand, 'TOJ')} Stk.`} />
                      <StatBox
                        label="Beratung"
                        value={`${getBucketUnits(demand, 'CONSULTING')} Std.`}
                      />
                      <StatBox
                        label="Training"
                        value={`${getBucketUnits(demand, 'TRAINING')} Tage`}
                      />
                      <StatBox label="Pilot" value={`${getBucketUnits(demand, 'PILOT')} Stk.`} />
                    </SimpleGrid>
                    {capacityFitMessage && (
                      <Alert.Root
                        status={capacityFitMessage.colorPalette === 'red' ? 'error' : 'warning'}
                      >
                        <Alert.Indicator />
                        <Alert.Content>
                          <Alert.Title>{capacityFitMessage.title}</Alert.Title>
                          <Alert.Description>{capacityFitMessage.text}</Alert.Description>
                        </Alert.Content>
                      </Alert.Root>
                    )}
                  </Stack>

                  <Stack gap={3}>
                    <Card.Title>Themen des Marktplatzangebots</Card.Title>

                    <HStack wrap="wrap">
                      {(demand.topic ?? '')
                        .split(',')
                        .filter(Boolean)
                        .map(topic => (
                          <Badge key={topic}>{getTopicLabel(topic.trim())}</Badge>
                        ))}
                    </HStack>
                  </Stack>
                </Stack>
              </Card.Body>
            </Card.Root>

            <Stack gap={6}>
              <WorkloadQuotaCard
                title="Ihr offenes Kontingent"
                values={{
                  TOJ: Math.round(getRemainingWorkloadUnits(workload, 'TOJ')),
                  CONSULTING: Math.round(getRemainingWorkloadUnits(workload, 'CONSULTING')),
                  TRAINING: Math.round(getRemainingWorkloadUnits(workload, 'TRAINING')),
                  PILOT: Math.round(getRemainingWorkloadUnits(workload, 'PILOT')),
                }}
              />

              <Card.Root>
                <Card.Body>
                  <Stack gap={6}>

                    <HStack justify="space-between">
                      <Card.Title>Ihre Themen</Card.Title>
                      <Button variant="ghost" onClick={() => navigate('/settings/profile')}>
                        <LuSettings />
                        Themen festlegen
                      </Button>
                    </HStack>

                    <HStack wrap="wrap">
                      {profile?.topics?.length ? (
                        profile.topics.map(topic => (
                          <Badge key={topic.code} colorPalette="gray">
                            {topic.nameDe}
                          </Badge>
                        ))
                      ) : (
                        <Text color="fg.muted">Keine Themen hinterlegt</Text>
                      )}
                    </HStack>
                  </Stack>
                </Card.Body>
              </Card.Root>

              <Card.Root>
                <Card.Body>
                  <Stat.Root>
                    <Stat.ValueText>{demand.interestsCount ?? 0}</Stat.ValueText>
                    <Stat.Label>SIB haben Interesse angemeldet</Stat.Label>
                  </Stat.Root>
                </Card.Body>
              </Card.Root>
            </Stack>
          </Grid>
        </Page.Content>
      </Page.Root>

      {isOpen && (
        <Card.Root
          variant="elevated"
          position="fixed"
          left="50%"
          bottom={8}
          transform="translateX(-50%)"
          zIndex={10}
        >
          <Card.Body>
            {hasSubmittedInterest ? (
              <Button
                colorPalette="red"
                variant="subtle"
                loading={isWithdrawing}
                onClick={handleWithdrawInterest}
              >
                Interesse zurückziehen
              </Button>
            ) : (
              <HStack gap={4}>
                <Button
                  variant="ghost"
                  colorPalette="red"
                  disabled={!canReject}
                  onClick={() => setShowRejectDialog(true)}
                >
                  Angebot ablehnen
                </Button>

                <Button
                  colorPalette="blue"
                  disabled={!canAccept}
                  loading={isAccepting}
                  onClick={handleAccept}
                >
                  Interesse melden
                </Button>
              </HStack>
            )}
          </Card.Body>
        </Card.Root>
      )}

      <RejectDemandDialog
        open={showRejectDialog}
        isSubmitting={isRejecting}
        error={error}
        onOpenChange={setShowRejectDialog}
        onSubmit={handleReject}
      />
      <InterestSubmittedDialog
        open={showInterestDialog}
        onClose={() => setShowInterestDialog(false)}
      />
    </>
  )
}
